import React, { useCallback, useEffect, useRef, useState } from 'react';

import { t } from 'i18next';

import { CLOSE_ICON } from '../../../constants/images';
import useOutsideComponentClick from '../../../hooks/useOutsideComponentClick/outsideComponentClick';
import { IGenericMenu } from '../../../interfaces/genericMenu';
import { GenericVoidFunction } from '../../../types/genericTypes';
import BdsCheckbox from '../BdsCheckbox/BdsCheckbox';
import BdsRadioButton from '../BdsRadioButton/BdsRadioButton';
import BdsButton from '../Buttons/BdsButton/BdsButton';
import BdsIconButton from '../Buttons/BdsIconButton/BdsIconButton';
import BdsCardContainer from '../Cards/BdsCardContainer/BdsCardContainer';
import BdsDivider from '../Globals/BdsDivider/BdsDivider';
import './GenericMenu.scss';

const GenericMenu = ({
  type,
  dropdownData,
  customData,
  title,
  setShowGenericMenu,
  className,
  showGenericMenu,
  company
}: IGenericMenu) => {
  const [state, setState] = useState<{
    filterSelected: string | null;
  }>({
    filterSelected: customData?.value || null
  });
  const genericMenuRef = useRef<HTMLDivElement>(null);

  const menuType = {
    custom: 'custom',
    dropdown: 'dropdown'
  };

  const handleCloseMenu = useCallback(() => {
    setShowGenericMenu(false);
  }, [setShowGenericMenu]);

  const handleSelection = (newValue: string) => {
    if (newValue === state.filterSelected) return;
    if (customData && type === menuType.custom) {
      setState({ ...state, filterSelected: newValue });
      customData.onChanged(newValue);
    }
  };

  const handleClearFilters = () => {
    const newValue = customData?.defaultValueOnFilterButton ? customData.value : '';
    handleSelection(newValue);
  };

  const handleActionDropdown = (action?: GenericVoidFunction) => {
    action && action();
    setShowGenericMenu(false);
  };

  useEffect(() => {
    setState({
      filterSelected: customData?.value || null
    });
  }, [company]);

  const stopPropagation = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  useOutsideComponentClick(genericMenuRef, handleCloseMenu);

  const classNameList = ['gmc-list-content', type].join(' ');
  const classNameItem = ['gmc-lc-item', type].join(' ');
  const renderIcon = (column: any) => {
    if (column.icon?.component) {
      return <column.icon.component.component />;
    } else if (column.icon?.url) {
      return <img src={column.icon.url || '/placeholder.svg'} alt={column.label} />;
    }
    return null;
  };

  const renderColumnSectionDropdown = (column: any, columnIndex: number, isLastColumn: boolean) => {
    return (
      <button
        key={`${column.label || 'column'}-${columnIndex}`}
        className={classNameItem}
        onClick={() => handleActionDropdown(column.action)}
      >
        <div className="item-label">
          <p>{column.label}</p>
          <div className="item-icon">{renderIcon(column)}</div>
        </div>
        {!isLastColumn && <BdsDivider size="s" color="high" />}
      </button>
    );
  };

  const renderDropdown = () => {
    return (
      <div className={classNameList}>
        {dropdownData?.sections.map((section, sectionIndex) => (
          <div key={`${section.title || 'section'}-${sectionIndex}`} className="gmc-dropdown">
            {section.title && <h3 className="gmc-dropdown-title">{section.title}</h3>}
            <div>
              {section.columns?.map((column, columnIndex) =>
                renderColumnSectionDropdown(
                  column,
                  columnIndex,
                  columnIndex === section.columns.length - 1 && sectionIndex == dropdownData?.sections.length - 1
                )
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const randerListWithCheckbox = () => {
    const data = customData?.data;
    const selectedValue = state.filterSelected;

    return (
      <div className={classNameList}>
        {data?.map((item: { name: string; value: string }) => (
          <button
            key={`${item.name || 'item'}-${data.indexOf(item)}`}
            className={classNameItem}
            onClick={() => handleSelection(item.value)}
          >
            <div className={`item-label ${selectedValue === item.value ? 'selected' : ''}`}>
              <p>{item.name}</p>
              {React.createElement(customData?.typeCheckbox === 'square' ? BdsCheckbox : BdsRadioButton, {
                variant: 'accent',
                onChange: () => handleSelection(item.value),
                selected: selectedValue === item.value,
                value: item.value,
                name: item.name
              })}
            </div>
            <BdsDivider size={'s'} color={'high'} />
          </button>
        ))}
      </div>
    );
  };

  return (
    <BdsCardContainer
      onClick={stopPropagation}
      ref={genericMenuRef}
      className={`generic-menu-container ${className} ${showGenericMenu ? 'displayed' : ''}`}
      paddingSize="padding-l"
      borderRadiusSize="radius-l"
    >
      <div className="gm-top">
        <div className={`gm-top icon-close-top ${type === menuType.dropdown ? 'dropdown' : ''}`}>
          <BdsIconButton icon={CLOSE_ICON} action={handleCloseMenu} variant="transparent-inverse" size="sm" />
        </div>
        {type !== menuType.dropdown && <h2 className="gmt-top title-top">{title}</h2>}
      </div>
      <div className="gm-center">
        {type === menuType.custom && randerListWithCheckbox()}
        {type === menuType.dropdown && renderDropdown()}
      </div>
      {type !== menuType.dropdown &&
        (type !== menuType.custom || (type === menuType.custom && customData?.showButtonFilter)) && (
          <div className="gm-bottom">
            <BdsButton variant="inverse" size="m" action={handleClearFilters}>
              {t('global.button.clear')}
            </BdsButton>
          </div>
        )}
    </BdsCardContainer>
  );
};

export default GenericMenu;
